import api from './api';
import Member from '@/types/Member';
import Punishment from '@/types/Punischment';
import Presence from '@/types/Presence';
import PunishmentCategory from '@/types/PunishmentCategory';
import Subsidy from '@/types/CashData';

export interface Season {
  year: number;
  members: Member[];
  fines: Punishment[];
  presences: Presence[];
  meetings: number;
  categories: PunishmentCategory[];
  subsidies: Subsidy[];
  // Strafen je Mitglied (Vorname wie in `fines`) aufgeteilt nach Kategorie
  fineDetails: Record<string, FineCategory[]>;
}

export interface FineCategory {
  type: string;
  value: number;
  count: number;
}

interface MemberPunishment {
  value: number;
  member: { firstName: string };
  punishment: { type: string };
}

const groupFines = (rows: MemberPunishment[]): Record<string, FineCategory[]> => {
  const byMember: Record<string, Record<string, FineCategory>> = {};
  for (const row of rows) {
    const name = row.member?.firstName;
    const type = row.punishment?.type;
    if (!name || !type) continue;
    const categories = (byMember[name] ??= {});
    const entry = (categories[type] ??= { type, value: 0, count: 0 });
    entry.value += row.value;
    entry.count += 1;
  }
  return Object.fromEntries(
    Object.entries(byMember).map(([name, categories]) => [
      name,
      Object.values(categories).sort((a, b) => b.value - a.value),
    ]),
  );
};

class SeasonService {
  async fetchSeason(year: number): Promise<Season> {
    const q = `?year=${year}`;
    const [members, fines, presences, presenceDates, categories, subsidies, fineRows] = await Promise.all([
      api.get<Member[]>('/members'),
      api.get<Punishment[]>(`/punishments/summary${q}`),
      api.get<Presence[]>(`/presences/summary${q}`),
      api.get<{ date: string }[]>(`/presences${q}`),
      api.get<PunishmentCategory[]>(`/punishments/categories${q}`),
      api.get<Subsidy[]>(`/subsidies${q}`),
      api.get<MemberPunishment[]>(`/punishments${q}`),
    ]);

    return {
      year,
      members,
      fines,
      presences,
      // Anzahl Stammtische = Anzahl unterschiedlicher Termine mit Anwesenheit
      meetings: new Set(presenceDates.map((p) => p.date.slice(0, 10))).size,
      categories,
      subsidies,
      fineDetails: groupFines(fineRows),
    };
  }
}

export default new SeasonService();
